import { FormField, RequestStatus, Streamfield, WagtailImageRendition, WagtailSvg } from '@/types';
import BannerImage from '@components/BannerImage';
import Button from '@components/Button';
import EmailField from '@components/Form/FormFields/EmailField';
import MultiLineField from '@components/Form/FormFields/MultiLineField';
import SelectField from '@components/Form/FormFields/SelectField';
import SingleLineField from '@components/Form/FormFields/SingleLineField';
import Hero from '@components/Hero';
import RawHtml from '@components/RawHtml';
import { BasePageProps, basePageWrap } from '@views/BasePage';
import { useState } from 'react';
import { getFooter, getHeader, getMenu, getPage, WagtailApiResponseError } from '@api/wagtail';
import { GetStaticProps } from 'next';
import { loaders, Streamfields as hey } from '@/streamfields';
import Streamfields from '@streamfields/Streamfields';
import clsx from 'clsx';
import CheckBoxField from '@components/Form/FormFields/CheckBoxField';
import CountersField from '@components/Form/FormFields/CountersField';

type FormApiErrors = Record<string, string[]>;

type Props = {
  svg: WagtailSvg;
  title: string;
  subheader: string;
  subtitle: string;
  before_form: Streamfield[];
  after_form: Streamfield[];
  thank_you_text: string;
  form_fields: FormField[];
  banner_image: WagtailImageRendition | null;
  mobile_banner_image: WagtailImageRendition | null;
} & BasePageProps;

function CustomFormPage(props: Props) {
  const {
    title = '',
    subheader,
    subtitle,
    form_fields: formFields,
    before_form: beforeForm,
    after_form: afterForm,
    thank_you_text: thankYouText,
    banner_image,
    mobile_banner_image,
    svg,
  } = props;

  const [status, setStatus] = useState<RequestStatus>('idle');
  const [errors, setErrors] = useState<FormApiErrors | null>(null);
  const [currentSelected, setCurrentSelected] = useState<string>('Meine Verträge');
  const [currentSubTopic, setCurrentSubTopic] = useState<string>('Meine Verträge');
  const [wasSubmitted, setWasSubmitted] = useState<boolean>(false);

  // this page only works with corresponding wagtail page, to remove, make sure to remove the wagtailpage but also this site here.
  // uses form fields of the wagtailpage at /online-services/kontakt
  // cleannames of forminputs in backendpage are relevant to handle the form submission, make sure inputs in frontend have the correct cleannames

  function mod97(digital: number | string) {
    const digi = digital.toString();
    let checksum: number | string = digi.slice(0, 2);
    let fragment = '';
    for (let offset = 2; offset < digi.length; offset += 7) {
      fragment = checksum + digi.substring(offset, offset + 7);
      checksum = parseInt(fragment, 10) % 97;
    }
    return checksum;
  }

  const validateIban = (input: string) => {
    const codeLengths = {
      DE: 22,
    };
    const iban = input.toUpperCase().replace(/[^A-Z0-9]/g, '');
    const code = iban.match(/^([A-Z]{2})(\d{2})([A-Z\d]+)$/);
    if (!code || iban.length !== (codeLengths as any)[code[1]]) {
      return false;
    }
    const digits = (code[3] + code[1] + code[2]).replace(/[A-Z]/g, (letter: string) => {
      return (letter.charCodeAt(0) - 55).toString();
    });
    return mod97(digits) === 1;
  };

  const validateVertragskonto = (value: string) => {
    return value.length === 0 || value.length === 10;
  };

  return (
    <div className='form-page'>
      <div
        className={clsx({
          'hero-content': banner_image,
        })}
      >
        {banner_image ? (
          <BannerImage image={banner_image} mobileImage={mobile_banner_image} />
        ) : (
          <div className='banner-image banner-image__small' />
        )}
        <Hero title={title} subtitle={subheader} text={subtitle} svg={svg} />
      </div>
      {beforeForm ? <Streamfields fields={beforeForm} /> : null}
      <form
        id='formular'
        className='form'
        onSubmit={async event => {
          event.preventDefault();
          setWasSubmitted(true);
          const formData = new FormData(event.currentTarget);

          const data: {
            [key: string]: FormDataEntryValue | FormDataEntryValue[];
          } = Object.fromEntries(formData);

          formFields.forEach(field => {
            if (field.field_type !== 'checkboxes') return;
            data[field.clean_name] = formData.getAll(field.clean_name);
          });
          if (
            (currentSelected !== 'Bankdaten ändern' || validateIban(data.iban as string)) &&
            validateVertragskonto(data.vertragskonto as string)
          ) {
            try {
              setStatus('loading');
              setErrors(null);
              const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/v2/forms/`, {
                method: 'POST',
                headers: {
                  Accept: 'application/json',
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  page: props.form_fields?.[0]?.page_id,
                  form_data: JSON.stringify(data),
                }),
              });

              if (res.ok) {
                setStatus('success');
              } else {
                const formErrors = (await res.json()) as FormApiErrors;
                setErrors(formErrors);
                setStatus('error');
              }
            } catch (error) {
              setStatus('error');
            }
          } else if (currentSelected === 'Bankdaten ändern' && !validateIban(data.iban as string)) {
            const elem = document.getElementById('iban');
            elem?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
          } else if (!validateVertragskonto(data.vertragskonto as string)) {
            const elem = document.getElementById('vertragskonto');
            elem?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
          }
        }}
      >
        <div
          className={clsx('form-field', { 'form-field--error': errors?.ihr_anliegen })}
          key={1}
          style={{ width: '100%' }}
        >
          <SelectField
            id={1}
            choices='Meine Verträge,Online Kundencenter,Monatliche Abschlagszahlung,Ratenzahlung vereinbaren,Rechnungskorrektur,Bankdaten ändern,Zählerstand mitteilen,Rechnung simulieren,Lob & Kritik,Sonstiges'
            clean_name='ihr_anliegen'
            label='Ihr Anliegen'
            default_value='Meine Verträge'
            field_type='dropdown'
            help_text=''
            sort_order={1}
            fieldset=''
            width={100}
            fields={[]}
            required
            setCurrentSelected={setCurrentSelected}
          />
        </div>

        {currentSelected === 'Monatliche Abschlagszahlung' ? (
          <>
            <p style={{ width: '100%' }}>
              Bitte beachten Sie, dass dieser nicht mehr als 20% vom vorherigen abweichen sollte
            </p>
            <div
              className={clsx('form-field', { 'form-field--error': errors?.monatlicher_abschlag })}
              key={13}
              style={{ width: '60%' }}
            >
              <SingleLineField
                id={13}
                clean_name='monatlicher_abschlag'
                label='Monatlicher Abschlag'
                field_type='singleline'
                help_text='in €'
                fields={[]}
                sort_order={2}
                fieldset=''
                choices=''
                width={60}
                required
              />
            </div>
          </>
        ) : null}

        {currentSelected === 'Ratenzahlung vereinbaren' ? (
          <>
            <div
              className={clsx('form-field', { 'form-field--error': errors?.rechnungsbetrag })}
              key={14}
              style={{ width: '60%' }}
            >
              <SingleLineField
                id={14}
                clean_name='rechnungsbetrag'
                label='Rechnungsbetrag'
                field_type='singleline'
                help_text='in €'
                fields={[]}
                sort_order={3}
                fieldset=''
                choices=''
                width={60}
                required
              />
            </div>
            <div
              className={clsx('form-field', {
                'form-field--error': errors?.gewunschte_anzahl_der_raten,
              })}
              key={15}
              style={{ width: '100%' }}
            >
              <SelectField
                id={15}
                clean_name='gewunschte_anzahl_der_raten'
                label='Gewünschte Anzahl der Raten'
                field_type='dropdown'
                help_text=''
                fields={[]}
                sort_order={4}
                fieldset=''
                choices='--bitte auswählen--,2,3,4,5,6'
                width={100}
                required
              />
            </div>
            <div
              className={clsx('form-field', { 'form-field--error': errors?.zahlungsweise })}
              key={16}
              style={{ width: '100%' }}
            >
              <SelectField
                id={16}
                choices='--bitte auswählen--,Ich bezahle per Überweisung,Bitte nutzen Sie das vorliegende SEPA-Mandat'
                clean_name='zahlungsweise'
                label='Zahlungsweise'
                default_value='Ich bezahle per Überweisung'
                field_type='dropdown'
                help_text=''
                sort_order={5}
                fieldset=''
                width={100}
                fields={[]}
                required
              />
            </div>
          </>
        ) : null}

        {currentSelected === 'Bankdaten ändern' ? (
          <>
            <p style={{ width: '100%' }}>Bitte tragen Sie Ihre aktuelle IBAN ein</p>
            <div
              className={clsx('form-field', { 'form-field--error': errors?.iban })}
              key={17}
              style={{ width: '100%' }}
            >
              <SingleLineField
                id={17}
                clean_name='iban'
                label='IBAN'
                field_type='singleline'
                help_text=''
                fields={[]}
                validation={validateIban}
                sort_order={6}
                fieldset=''
                choices=''
                width={100}
                required
                submitted={wasSubmitted}
              />
            </div>
          </>
        ) : null}

        {currentSelected === 'Zählerstand mitteilen' ? (
          <>
            <div
              className={clsx('form-field', { 'form-field--error': errors?.grund })}
              key={19}
              style={{ width: '100%' }}
            >
              <SelectField
                id={19}
                choices='Anmeldung (Einzug),Abmeldung (Auszug),Zwischenablesung,Versorgerwechsel'
                clean_name='grund'
                label='Grund'
                default_value='Anmeldung (Einzug)'
                setCurrentSelected={setCurrentSubTopic}
                field_type='dropdown'
                help_text=''
                sort_order={7}
                fieldset=''
                width={100}
                fields={[]}
                required
              />
            </div>
            <div
              className={clsx('form-field', { 'form-field--error': errors?.zahlerstand })}
              key={18}
              style={{ width: '100%' }}
            >
              <CountersField
                id={18}
                clean_name='zahlerstand'
                label='Zählerstand'
                field_type='counters'
                help_text=''
                fields={[]}
                sort_order={8}
                fieldset=''
                choices='Strom,Gas,Wasser,Sonstige'
                width={100}
                removeYearInput={currentSubTopic === 'Abmeldung (Auszug)'}
                isCustomForm
                required
              />
            </div>
          </>
        ) : null}

        {currentSelected === 'Rechnungskorrektur' ? (
          <div
            className={clsx('form-field', { 'form-field--error': errors?.rechnungsnummer })}
            key={21}
            style={{ width: '100%' }}
          >
            <SingleLineField
              id={21}
              clean_name='rechnungsnummer'
              label='Rechnungsnummer'
              field_type='singleline'
              help_text=''
              fields={[]}
              sort_order={9}
              fieldset=''
              choices=''
              width={100}
              required={false}
            />
          </div>
        ) : null}

        <div
          className={clsx('form-field', {
            'form-field--error': errors?.beschreibung_ihres_anliegens,
          })}
          key={2}
          style={{ width: '100%' }}
        >
          <MultiLineField
            id={2}
            clean_name='beschreibung_ihres_anliegens'
            label='Beschreibung Ihres Anliegens'
            field_type='multiline'
            help_text=''
            sort_order={10}
            fieldset=''
            width={100}
            fields={[]}
            choices=''
            required={false}
          />
        </div>
        <div
          className={clsx('form-field', { 'form-field--error': errors?.vertragskonto })}
          key={3}
          style={{ width: '100%' }}
        >
          <SingleLineField
            id={3}
            clean_name='vertragskonto'
            label='Vertragskonto'
            field_type='singleline'
            help_text='Siehe Rechnung'
            sort_order={11}
            fieldset=''
            validation={validateVertragskonto}
            width={100}
            fields={[]}
            choices=''
            required={false}
            submitted={wasSubmitted}
          />
        </div>
        <div
          className={clsx('form-field', { 'form-field--error': errors?.anrede })}
          key={4}
          style={{ width: '52%' }}
        >
          <SelectField
            id={4}
            choices='Frau,Herr,Divers,keine Angabe'
            clean_name='anrede'
            label='Anrede'
            default_value='Frau'
            field_type='dropdown'
            help_text=''
            sort_order={12}
            fieldset=''
            width={52}
            fields={[]}
            required
          />
        </div>
        <div
          className={clsx('form-field', { 'form-field--error': errors?.vorname })}
          key={5}
          style={{ width: '48.5%' }}
        >
          <SingleLineField
            id={5}
            clean_name='vorname'
            label='Vorname'
            field_type='singleline'
            help_text=''
            sort_order={13}
            fieldset=''
            width={48.5}
            fields={[]}
            choices=''
            required
          />
        </div>
        <div
          className={clsx('form-field', { 'form-field--error': errors?.nachname })}
          key={6}
          style={{ width: '48.5%' }}
        >
          <SingleLineField
            id={6}
            clean_name='nachname'
            label='Nachname'
            field_type='singleline'
            help_text=''
            sort_order={14}
            fields={[]}
            fieldset=''
            width={48.5}
            choices=''
            required
          />
        </div>
        <div
          className={clsx('form-field', { 'form-field--error': errors?.straxdfe })}
          key={7}
          style={{ width: '60%' }}
        >
          <SingleLineField
            id={7}
            clean_name='straxdfe'
            label='Straße'
            field_type='singleline'
            help_text=''
            sort_order={15}
            fieldset=''
            fields={[]}
            width={60}
            choices=''
            required
          />
        </div>
        <div
          className={clsx('form-field', { 'form-field--error': errors?.hausnummer })}
          key={8}
          style={{ width: '37%' }}
        >
          <SingleLineField
            id={8}
            clean_name='hausnummer'
            label='Hausnummer'
            field_type='singleline'
            help_text=''
            sort_order={16}
            fieldset=''
            width={37}
            fields={[]}
            choices=''
            required
          />
        </div>
        <div
          className={clsx('form-field', { 'form-field--error': errors?.hausnummer })}
          key={9}
          style={{ width: '37%' }}
        >
          <SingleLineField
            id={9}
            clean_name='plz'
            label='PLZ'
            field_type='singleline'
            help_text=''
            fields={[]}
            sort_order={17}
            fieldset=''
            width={37}
            choices=''
            required
          />
        </div>
        <div
          className={clsx('form-field', { 'form-field--error': errors?.ort })}
          key={10}
          style={{ width: '60%' }}
        >
          <SingleLineField
            id={10}
            clean_name='ort'
            label='Ort'
            field_type='singleline'
            help_text=''
            sort_order={18}
            fields={[]}
            fieldset=''
            width={60}
            choices=''
            required
          />
        </div>
        <div
          className={clsx('form-field', { 'form-field--error': errors?.e_mail })}
          key={11}
          style={{ width: '100%' }}
        >
          <EmailField
            id={11}
            clean_name='e_mail'
            label='E-Mail'
            field_type='email'
            help_text=''
            fields={[]}
            sort_order={19}
            fieldset=''
            width={100}
            choices=''
            required
          />
        </div>
        <div
          className={clsx('form-field', { 'form-field--error': errors?.telefonnummer })}
          key={12}
          style={{ width: '100%' }}
        >
          <SingleLineField
            id={12}
            clean_name='telefonnummer'
            label='Telefonnummer'
            field_type='singleline'
            help_text=''
            fields={[]}
            sort_order={20}
            fieldset=''
            choices=''
            width={60}
            required
          />
        </div>

        <div
          className={clsx('form-field', {
            'form-field--error': errors?.einwilligung_zur_datenverarbeitung,
          })}
          key={20}
          style={{ width: '100%' }}
        >
          <CheckBoxField
            id={20}
            choices=''
            clean_name='einwilligung_zur_datenverarbeitung'
            label='Einwilligung zur Datenverarbeitung'
            field_type='checkbox'
            help_text='Hiermit stimme ich der Verarbeitung meiner Daten zu. Ich kann meine Zustimmung jederzeit mit Wirkung für die Zukunft widerrufen. Weitere Informationen in der <a href="/datenschutz" target="_blank">Datenschutzerklärung</a>.'
            sort_order={21}
            fieldset=''
            width={100}
            fields={[]}
            required
          />
        </div>
        <p>* Pflichtfelder</p>
        {status === 'success' ? (
          <div className='message message--success'>
            <RawHtml html={thankYouText} />
          </div>
        ) : null}
        {!errors && status === 'error' ? (
          <div className='message message--error'>
            <p>Leider ist etwas schief gegangen... Bitte versuchen Sie es später erneut...</p>
            <p>
              Alternativ können Sie Ihre Anfrage auch direkt an die E-Mail-Adresse{' '}
              <a href='mailto:kundenservice@stadtwerke-witten.de'>
                kundenservice@stadtwerke-witten.de
              </a>{' '}
              senden.
            </p>
          </div>
        ) : null}
        {errors && status === 'error' ? (
          <div className='message message--error'>
            <p>Ihre Anfrage konnte nicht bearbeitet werden, bitte füllen Sie alle Felder aus.</p>
            <p>
              Alternativ können Sie Ihre Anfrage auch direkt an die E-Mail-Adresse{' '}
              <a href='mailto:kundenservice@stadtwerke-witten.de'>
                kundenservice@stadtwerke-witten.de
              </a>{' '}
              senden.
            </p>
          </div>
        ) : null}
        <div className='form-page__submit'>
          <Button
            primary
            type='submit'
            disabled={status === 'loading' || status === 'success'}
            className='form__submit'
          >
            Absenden
          </Button>
        </div>
      </form>
      {afterForm ? <Streamfields fields={afterForm} /> : null}
    </div>
  );
}

export default basePageWrap<Props>(CustomFormPage);

export const getStaticProps: GetStaticProps = async context => {
  const { params } = context;
  let path = params?.path ?? [];
  if (Array.isArray(path)) path = path.join('/');

  try {
    const { json: pageData } = await getPage('/online-services/kontakt');
    const [{ json: footerData }, { json: headerData }, { json: menuData }] = await Promise.all([
      getFooter(),
      getHeader(),
      getMenu(),
    ]);

    const blocksLoaderData = new Map<string, unknown>();
    let streamfields: Streamfield[] = [
      // @ts-expect-error
      ...(pageData.content ?? []),
      // @ts-expect-error
      ...(pageData.before_form ?? []),
      // @ts-expect-error
      ...(pageData.after_form ?? []),
    ];

    const containerFields = streamfields.filter(field => field.type === 'container');
    // eslint-disable-next-line no-restricted-syntax
    for (const field of containerFields) {
      streamfields = streamfields.concat(field?.value?.content ?? []);
    }

    if (streamfields.length) {
      // eslint-disable-next-line no-restricted-syntax
      for (const streamfield of streamfields) {
        const blockLoader = loaders[streamfield.type as hey];
        // eslint-disable-next-line no-await-in-loop
        const data = await blockLoader?.(streamfield.value);
        if (data) blocksLoaderData.set(streamfield.id, data);
      }
    }

    return {
      props: {
        ...pageData,
        footerData,
        headerData,
        menuData,
      },
      revalidate: 3600,
    };
  } catch (err) {
    if (!(err instanceof WagtailApiResponseError)) {
      throw err;
    }

    if (err.response.status >= 500) {
      throw err;
    }

    return { notFound: true };
  }
};
